import { Timestamp } from '@angular/fire/firestore';

/** Firestore Timestamp as returned by the client SDK or plain seconds/nanoseconds. */
export type FirestoreTimestampLike = {
  toMillis?: () => number;
  toDate?: () => Date;
  seconds?: number;
  nanoseconds?: number;
};

/** Value that may be stored as int64 ms or Firestore Timestamp (during/after migration). */
export type FirestoreTimestampField =
  | number
  | Timestamp
  | FirestoreTimestampLike
  | Date
  | null
  | undefined;

const FIRESTORE_TIMESTAMP_FIELD_NAMES = new Set([
  'createdOn',
  'updatedOn',
  'createdAt',
  'updatedAt',
  'created',
  'updated',
  'startDate',
  'endDate',
  'slotId',
  'timeFrom',
  'timeTo',
  'lastInvoicedDate',
  'submittedAt',
  'expiration',
  'createdDate',
  'updatedDate',
]);

export function timestampToMillis(value: unknown): number | null {
  if (value == null) return null;
  if (value instanceof Timestamp) return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  if (typeof value === 'object') {
    const o = value as FirestoreTimestampLike;
    if (typeof o.toMillis === 'function') return o.toMillis();
    if (typeof o.toDate === 'function') return o.toDate().getTime();
    if (typeof o.seconds === 'number') {
      return o.seconds * 1000 + Math.floor((o.nanoseconds ?? 0) / 1_000_000);
    }
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const asNum = Number(trimmed);
    if (!Number.isNaN(asNum) && Number.isFinite(asNum)) return asNum;
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
  }

  return null;
}

export function timestampToDate(value: unknown): Date | null {
  const ms = timestampToMillis(value);
  return ms == null ? null : new Date(ms);
}

export function firestoreTimestampNow(): Timestamp {
  return Timestamp.now();
}

export function firestoreTimestampFromMillis(ms: number): Timestamp {
  return Timestamp.fromMillis(ms);
}

export function millisToFirestoreTimestamp(value: unknown): Timestamp | null {
  if (value instanceof Timestamp) return value;
  const ms = timestampToMillis(value);
  return ms == null ? null : Timestamp.fromMillis(ms);
}

function isPlainTimestampMap(value: Record<string, unknown>): boolean {
  if (typeof value['seconds'] !== 'number') return false;
  if (typeof value['nanoseconds'] !== 'number') return false;
  if (typeof (value as FirestoreTimestampLike).toMillis === 'function') return false;
  if (typeof (value as FirestoreTimestampLike).toDate === 'function') return false;
  const keys = Object.keys(value);
  return keys.every((k) => k === 'seconds' || k === 'nanoseconds');
}

function isFirestoreFieldValue(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    '_methodName' in (value as Record<string, unknown>)
  );
}

export function normalizeFirestoreDataForWrite(value: unknown): unknown {
  if (value == null) return value;
  if (isFirestoreFieldValue(value)) return value;
  if (value instanceof Timestamp) return value;
  if (value instanceof Date) return Timestamp.fromDate(value);

  if (Array.isArray(value)) {
    return value.map((item) => normalizeFirestoreDataForWrite(item));
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (isPlainTimestampMap(obj)) {
      const ms = timestampToMillis(obj);
      return ms == null ? value : Timestamp.fromMillis(ms);
    }

    const out: Record<string, unknown> = {};
    for (const key of Object.keys(obj)) {
      const child = obj[key];
      if (child === undefined) continue;

      if (FIRESTORE_TIMESTAMP_FIELD_NAMES.has(key)) {
        const ts = millisToFirestoreTimestamp(child);
        out[key] = ts ?? normalizeFirestoreDataForWrite(child);
        continue;
      }

      if (key === 'date' && typeof child === 'number') {
        const ts = millisToFirestoreTimestamp(child);
        out[key] = ts ?? child;
        continue;
      }

      out[key] = normalizeFirestoreDataForWrite(child);
    }
    return out;
  }

  return value;
}

export function removeUndefinedForFirestore(obj: unknown): unknown {
  if (obj === null || obj === undefined) return null;
  if (isFirestoreFieldValue(obj)) return obj;
  if (obj instanceof Timestamp) return obj;
  if (obj instanceof Date) return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => removeUndefinedForFirestore(item));
  }

  if (typeof obj === 'object') {
    const record = obj as Record<string, unknown>;
    const cleaned: Record<string, unknown> = {};
    for (const key of Object.keys(record)) {
      const value = record[key];
      if (value !== undefined) {
        cleaned[key] = removeUndefinedForFirestore(value);
      }
    }
    return cleaned;
  }

  return obj;
}

export function prepareFirestoreWrite(data: unknown): unknown {
  return removeUndefinedForFirestore(normalizeFirestoreDataForWrite(data));
}

export function formatFirestoreDateTime(value: unknown): string {
  const date = timestampToDate(value);
  if (!date) return '';
  return (
    date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }) +
    ' - ' +
    date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  );
}

export function formatFirestoreDateLong(value: unknown): string {
  const date = timestampToDate(value);
  if (!date) return '';
  const dateStr = date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return `${dateStr} ${timeStr}`;
}
